import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { CreditPackCard } from './CreditPackCard';
import { useEntitlements } from '@/hooks/use-entitlements';
import { useCheckout } from '@/hooks/use-checkout';
import { CREDIT_PACKS, type CreditPackConfig } from '@/types/credit-packs';

interface CreditPurchaseDialogProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CreditPurchaseDialog({
  children,
  open,
  onOpenChange,
}: CreditPurchaseDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const { credits } = useEntitlements();
  const { initiateCreditCheckout, isLoading, error } = useCheckout();

  const isOpen = open ?? internalOpen;
  const setIsOpen = onOpenChange ?? setInternalOpen;

  const handleSelectPack = async (pack: CreditPackConfig) => {
    await initiateCreditCheckout(pack.sku);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="credit-purchase-dialog"
      >
        <DialogHeader>
          <DialogTitle>Buy Credits</DialogTitle>
          <DialogDescription>
            {credits > 0
              ? `You have ${credits} credits remaining. Purchase more to unlock additional content.`
              : 'Purchase credits to unlock premium content.'}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive" data-testid="checkout-error">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="grid gap-4 py-4 sm:grid-cols-3">
          {CREDIT_PACKS.map((pack) => (
            <CreditPackCard
              key={pack.sku}
              pack={pack}
              onSelect={handleSelectPack}
              isLoading={isLoading}
              disabled={isLoading}
            />
          ))}
        </div>

        <p className="text-center text-xs text-muted-foreground">
          Credits never expire. One-time purchase.
        </p>
      </DialogContent>
    </Dialog>
  );
}
